import type { AssessorRecord, RecorderDocument, RecorderSnapshot } from './types';

export type AssessmentValues = {
  land: number | null;
  improvements: number | null;
  fixtures: number | null;
  personal: number | null;
  total: number | null;
};

export function assessmentAmount(value: string | undefined) {
  const text = value?.trim().replace(/^\$\s*/, '');
  if (!text || !/^(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d+)?$/.test(text)) return null;
  const amount = Number(text.replaceAll(',', ''));
  return Number.isFinite(amount) && amount <= Number.MAX_SAFE_INTEGER ? amount : null;
}

function normalizeKeys(fields: Record<string, string>) {
  return Object.fromEntries(
    Object.entries(fields).map(([key, value]) => [
      key.toLowerCase().replaceAll('_', ' ').trim(),
      value,
    ]),
  );
}

export function assessmentValues(fields: Record<string, string>): AssessmentValues {
  const normalized = normalizeKeys(fields);
  const land = assessmentAmount(normalized['assessed land value']);
  const improvements = assessmentAmount(normalized['assessed improvement value']);
  const fixtures = assessmentAmount(normalized['assessed fixtures value']);
  const personal = assessmentAmount(normalized['assessed personal property value']);
  const parts = [land, improvements, fixtures, personal];
  const sum = parts.reduce<number>((total, value) => total + (value ?? 0), 0);
  const total =
    parts.every((value) => value !== null) && sum <= Number.MAX_SAFE_INTEGER ? sum : null;
  return { land, improvements, fixtures, personal, total };
}

export function assessmentYear(year: string, now = new Date()) {
  // Closed rolls cannot precede the city's incorporation or extend into a future fiscal year.
  const value = /^\d{4}(?:\.0+)?$/.test(year.trim()) ? Number(year) : null;
  return value !== null && value >= 1850 && value <= now.getUTCFullYear() ? value : null;
}

/**
 * Proposition 13 caps the annual increase of a factored base year value at 2%.
 * A current value further from the latest closed roll than this cannot be an
 * inflation adjustment: it is a reassessment (or a reduction) the roll predates.
 */
export const REASSESSMENT_THRESHOLD = 0.025;

export type AssessmentDivergence =
  | 'consistent'
  | 'reassessed'
  | 'reduced'
  | 'unpublished'
  | 'unavailable';

export type OwnershipTransfer = {
  documentNumber: string;
  documentDate: string;
  names: string[];
  sourceUrl: string;
};

export type AssessorConflict = {
  /** Stable identifier, also the translation key. */
  id: 'useTypeUnits' | 'propertyClass' | 'units' | 'stories' | 'bathrooms' | 'yearBuilt';
  current: string;
  published: string;
};

export type AssessmentReconciliation = {
  published: { year: number; values: AssessmentValues } | null;
  current: AssessmentValues | null;
  /** Relative change from the latest published total to the current total. */
  divergence: number | null;
  status: AssessmentDivergence;
  /** Most recent recorded transfer deed close enough to explain the divergence. */
  transfer: OwnershipTransfer | null;
  conflicts: AssessorConflict[];
};

// Use classes describe their unit range in prose, e.g. "Apartment 5 to 14 Units".
export function useTypeUnits(useType: string | undefined) {
  const text = useType?.toLowerCase() ?? '';
  const range = /(\d+)\s*(?:to|through|[-\u2013])\s*(\d+)\s*units?/.exec(text);
  if (range) return { min: Number(range[1]), max: Number(range[2]) };
  const atMost = /(\d+)\s*units?\s*or\s*(?:less|fewer)/.exec(text);
  if (atMost) return { min: 1, max: Number(atMost[1]) };
  const atLeast = /(\d+)\s*units?\s*or\s*more/.exec(text);
  if (atLeast) return { min: Number(atLeast[1]), max: Number.POSITIVE_INFINITY };
  const single = /single[- ]family|one\s*unit|1\s*unit\b/.exec(text);
  if (single) return { min: 1, max: 1 };
  return null;
}

function count(value: string | undefined) {
  if (!value?.trim()) return null;
  const number = Number(value.replaceAll(',', ''));
  return Number.isFinite(number) && number >= 0 ? number : null;
}

function latestRoll(assessor: AssessorRecord, now: Date) {
  const dated = (assessor.history ?? []).flatMap((roll) => {
    const year = assessmentYear(roll.year, now);
    return year === null ? [] : [{ year, roll }];
  });
  if (dated.length === 0) return null;
  const year = Math.max(...dated.map((entry) => entry.year));
  const rows = dated.filter((entry) => entry.year === year);
  // Duplicate rows for one year cannot be reconciled against a single value.
  return rows.length === 1 ? rows[0]! : null;
}

/**
 * Escape assessments may correct a roll for up to four years after the
 * change of ownership the assessor missed, so a transfer that old can still
 * explain a divergence from the published roll (lien date January 1).
 */
const TRANSFER_LOOKBACK_YEARS = 4;

function transferWindowStart(rollYear: number) {
  return Date.UTC(rollYear - TRANSFER_LOOKBACK_YEARS, 0, 1);
}

function documentTime(value: string) {
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s|$)/.exec(value.trim());
  if (us) return Date.UTC(Number(us[3]), Number(us[1]) - 1, Number(us[2]));
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (iso) return Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  return null;
}

/**
 * A recorded DEED (grant deed, quitclaim, trustee's deed) is a change of
 * ownership. Security instruments and their releases are not.
 */
export function isTransferDeed(document: RecorderDocument) {
  return document.titles.some((title) => {
    const text = title.toUpperCase();
    return (
      /\bDEED\b/.test(text) &&
      !/TRUST\b|RECONVEYANCE|ASSIGNMENT|SUBSTITUTION|MODIFICATION|RELEASE|NOTICE/.test(text)
    );
  });
}

/**
 * Special tax (Mello-Roos) lien notices for a community facilities district
 * such as SFUSD's are indexed against every parcel in the district, so they
 * say nothing about this property in particular.
 */
export function isDistrictWideRecording(document: RecorderDocument) {
  return (
    document.titles.some((title) => /SPCL TAX LIEN/i.test(title)) &&
    document.names.some(
      (name) =>
        name.startsWith('(E)') &&
        /\b(SFUSD|UNIFIED SCHOOL|COMMUNITY FACILITIES|CFD|CITY (?:AND|&) COUNTY)\b/i.test(name),
    )
  );
}

export function latestTransfer(
  recorder: RecorderSnapshot | null | undefined,
  after: number | null,
): OwnershipTransfer | null {
  let best: { time: number; document: RecorderDocument } | null = null;
  for (const document of recorder?.documents ?? []) {
    if (!isTransferDeed(document)) continue;
    const time = documentTime(document.documentDate);
    if (time === null || (after !== null && time <= after)) continue;
    if (!best || time > best.time) best = { time, document };
  }
  return best
    ? {
        documentNumber: best.document.documentNumber,
        documentDate: best.document.documentDate,
        names: best.document.names,
        sourceUrl: best.document.sourceUrl,
      }
    : null;
}

export function assessorConflicts(
  current: Record<string, string>,
  published: Record<string, string> | null,
): AssessorConflict[] {
  const conflicts: AssessorConflict[] = [];
  const currentUnits = count(current.Units);
  const useType = current['Use type'];
  const declared = useTypeUnits(useType);
  if (
    useType &&
    currentUnits !== null &&
    currentUnits > 0 &&
    declared &&
    (currentUnits < declared.min || currentUnits > declared.max)
  )
    conflicts.push({
      id: 'useTypeUnits',
      current: useType,
      published: String(currentUnits),
    });
  if (!published) return conflicts;
  const roll = normalizeKeys(published);
  const publishedClass = roll['property class code definition'];
  if (
    useType &&
    publishedClass &&
    useType.toLowerCase().replace(/\s+/g, ' ') !== publishedClass.toLowerCase().replace(/\s+/g, ' ')
  )
    conflicts.push({
      id: 'propertyClass',
      current: useType,
      published: publishedClass,
    });
  const numeric: Array<{
    id: Exclude<AssessorConflict['id'], 'useTypeUnits' | 'propertyClass'>;
    current: string | undefined;
    published: string | undefined;
  }> = [
    { id: 'units', current: current.Units, published: roll['number of units'] },
    {
      id: 'stories',
      current: current.Stories,
      published: roll['number of stories'],
    },
    {
      id: 'bathrooms',
      current: current.Bathrooms,
      published: roll['number of bathrooms'],
    },
    {
      id: 'yearBuilt',
      current: current['Year built'],
      published: roll['year property built'],
    },
  ];
  for (const entry of numeric) {
    const a = count(entry.current);
    const b = count(entry.published);
    // Zero is how both sources record "not collected"; it is not a disagreement.
    if (a === null || b === null || a === 0 || b === 0 || a === b) continue;
    conflicts.push({ id: entry.id, current: String(a), published: String(b) });
  }
  return conflicts;
}

export function reconcileAssessment(
  assessor: AssessorRecord | null | undefined,
  recorder?: RecorderSnapshot | null,
  now = new Date(),
): AssessmentReconciliation {
  if (!assessor)
    return {
      published: null,
      current: null,
      divergence: null,
      status: 'unavailable',
      transfer: null,
      conflicts: [],
    };
  const latest = latestRoll(assessor, now);
  const published = latest
    ? { year: latest.year, values: assessmentValues(latest.roll.fields) }
    : null;
  const current =
    Object.keys(assessor.fields).length > 0 ? assessmentValues(assessor.fields) : null;
  const divergence =
    published?.values.total && current?.total !== null && current
      ? (current.total - published.values.total) / published.values.total
      : null;
  const status: AssessmentDivergence =
    divergence === null
      ? published || current
        ? 'unpublished'
        : 'unavailable'
      : divergence > REASSESSMENT_THRESHOLD
        ? 'reassessed'
        : divergence < -REASSESSMENT_THRESHOLD
          ? 'reduced'
          : 'consistent';
  const transfer =
    status === 'reassessed' || status === 'reduced'
      ? latestTransfer(recorder, published ? transferWindowStart(published.year) : null)
      : null;
  return {
    published,
    current,
    divergence,
    status,
    transfer,
    conflicts: assessorConflicts(assessor.fields, latest?.roll.fields ?? null),
  };
}
